import React, { useEffect, useState } from 'react';
import { useField } from 'formik';
import { NumericFormat } from 'react-number-format';
import classNames from '../../../utility/classNames';
import DescriptionTooltipModal from './DescriptionTooltipModal';
import Tooltip from '../../Tooltip';

function UnitTimeInput({
  label,
  layout = 'default',
  type = 'text',
  showAltValue = false,
  description,
  tooltipHeading,
  tooltipFormula,
  externalData,
  required,
  selectOptions,
  preValue,
  ...props
}) {
  const [field, meta, helpers] = useField(props);
  const { value, touched } = meta;

  const { setValue, setTouched } = helpers;
  const [unitOption, setUnitOption] = useState(value?.unit || selectOptions[0]);

  const [error, setError] = useState(undefined);

  useEffect(() => {
    if (preValue) {
      setValue(preValue);
    }
  }, [preValue]);

  useEffect(() => {
    if (!required) return;

    if (touched) {
      if (!value) {
        setError('This field is required');
      } else {
        setError(undefined);
      }
    }
  }, [touched, value]);

  //sourceInfo is an argument required by react-number-format to indicate if the change
  //is coming from user input or a change in props
  //https://s-yadav.github.io/react-number-format/docs/quirks#sourceInfo

  const handleUnitSelect = (e) => {
    e.preventDefault();
    setUnitOption(e.target.value);
    setValue({ unit: e.target.value, value: value?.value });
  };

  const handleValueChange = (values, sourceInfo) => {
    const { floatValue } = values;

    const unit = unitOption;
    // if user empyty the input, set value to null
    if (!floatValue && floatValue !== 0) {
      setValue(null);
      return;
    }
    setValue({ unit: unit, value: floatValue });
    setTouched(true);
  };

  return (
    <div
      className={classNames(
        (layout === 'compact' || layout === 'tableNoLabel') && 'my-0 space-y-0',
        layout === 'tableNoLabel' && 'w-full'
      )}
    >
      <div
        className={classNames(
          'sm:grid sm:items-start sm:gap-2',
          (layout === 'compact' || layout === 'tableNoLabel') &&
            'sm:gap-4 pt-0 border-none',
          layout === 'tableNoLabel' ? 'sm:grid-cols-2' : 'sm:grid-cols-3-inputs'
        )}
      >
        {layout !== 'tableNoLabel' && (
          <label
            htmlFor={field.name}
            className={classNames(
              'h-full flex',
              description || (touched && error)
                ? 'items-start'
                : 'items-center',
              layout === 'stacked' && 'col-span-3'
            )}
          >
            <span className="text-md font-normal text-neutral-900">
              {label}
              {required && <span title="required">*</span>}{' '}
              {description && layout === 'compact' && (
                <DescriptionTooltipModal description={description} />
              )}
              {description && layout !== 'compact' && (
                <Tooltip
                  text={description}
                  heading={tooltipHeading}
                  formula={tooltipFormula}
                  classes="whitespace-normal"
                  type="info"
                />
              )}
            </span>
          </label>
        )}

        <div
          className={classNames(
            layout === 'stacked' ? 'col-span-3' : 'col-span-2',
            'rounded'
          )}
        >
          <div className="flex max-w-lg w-full">
            <div className={classNames('block w-full relative mr-2')}>
              <NumericFormat
                id={field.name}
                value={value?.value}
                onValueChange={handleValueChange}
                displayType="input"
                thousandSeparator={true}
                decimalScale={2}
                allowNegative={false}
                onBlur={() => setTouched(true)}
                type={type}
                {...props}
                className={classNames(
                  'block w-full border focus:border-primary bg-neutral-input focus:outline-none focus:ring-transparent  text-md  h-8 rounded-md py-0 px-3 placeholder-neutral-500 placeholder-opacity-90',
                  touched && error ? 'border-warning' : 'border-transparent'
                )}
              />
              {unitOption && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span
                    className="text-neutral-900 text-md tracking-wide"
                    id="price-currency"
                  >
                    {unitOption}
                  </span>
                </div>
              )}
            </div>

            <select
              value={unitOption}
              onChange={handleUnitSelect}
              className="block  w-[128px] pl-4 pr-7 border-transparent border bg-neutral-input focus:outline-none focus:ring-transparent focus:border-neutral-900  text-md  h-8 rounded-md py-0 px-3 placeholder-neutral-500 placeholder-opacity-90"
            >
              {selectOptions?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {touched && error ? (
            <div className="error text-base font-small text-warning ">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default UnitTimeInput;
